/**
 * What: Strips <!--[if ...]>...<!--[endif]--> blocks.
 * Why:  Word uses these for backwards-compat; browsers ignore them but they add noise.
 */
export function removeWordConditionalComments(html: string): string {
  return html.replace(/<!--\[if[^\]]*\]-->[\s\S]*?<!--\[endif\]-->/gi, '')
}

import { canonicalStyleClass } from './bayer-vocabulary'

/** Strips all <style> tags from the HTML. */
export function removeEmbeddedStyleTags(html: string): string {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  tempDiv.querySelectorAll('style').forEach((styleEl) => {
    styleEl.remove()
  })

  return tempDiv.innerHTML
}

/**
 * What: Replaces · (U+00B7) with • (U+2022) inside Symbol-font spans.
 * Why:  Word's Symbol font renders middle-dot as a bullet glyph; browsers don't.
 */
export function replaceSymbolBulletsOnDom(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>('span').forEach((span) => {
    if (!(/symbol/i.test(span.style.fontFamily) && span.textContent?.includes('\u00B7'))) return
    span.textContent = span.textContent.replace(/\u00B7/g, '\u2022')
    span.style.fontFamily = ''
  })
}

/** Returns the Symbol-font bullet span inside a <p>, or null if none found. */
function findSymbolBulletSpan(p: Element): HTMLElement | null {
  for (const span of Array.from(p.querySelectorAll<HTMLElement>('span'))) {
    if (/symbol/i.test(span.style.fontFamily) && span.textContent?.includes('\u00B7')) {
      return span
    }
  }
  return null
}

/**
 * What: Converts consecutive <p> elements with Symbol-font bullets into <ul><li>.
 * Why:  Word uses <p> + Symbol middle-dot to simulate bullets, but insertHtml()
 *       only registers real <ul><li> as list items.
 * Run BEFORE replaceSymbolBulletsOnDom — this removes the Symbol spans entirely.
 *
 * @param options.skipBayerStyled  When true, paragraphs that carry a Bayer Named
 *   Style class are left intact so Word resolves the style on insert (hanging
 *   indent + spacing come from the Named Style). Set for the insert path only;
 *   the bubble path converts all Symbol-bullet paragraphs so they render
 *   as visible list items in the browser preview.
 */
export function convertSymbolBulletsToListOnDom(
  root: ParentNode,
  options?: { skipBayerStyled?: boolean }
): void {
  const allParagraphs = Array.from(root.querySelectorAll('p'))
  const processed = new Set<Element>()

  for (const p of allParagraphs) {
    if (processed.has(p) || !findSymbolBulletSpan(p)) continue
    if (
      options?.skipBayerStyled &&
      Array.from(p.classList).some((c) => canonicalStyleClass(c) !== null)
    ) {
      continue
    }

    // Collect consecutive bullet paragraphs
    const group: HTMLParagraphElement[] = [p]
    processed.add(p)

    let next = p.nextElementSibling
    while (next?.tagName === 'P' && findSymbolBulletSpan(next)) {
      group.push(next as HTMLParagraphElement)
      processed.add(next)
      next = next.nextElementSibling
    }

    const ul = document.createElement('ul')

    for (const bp of group) {
      const li = document.createElement('li')

      // Remove the Symbol bullet span (takes its spacing child with it)
      findSymbolBulletSpan(bp)?.remove()

      // Move content — text formatting lives in inner spans/tags.
      // No paragraph styles carried; Word applies its own list style.
      while (bp.firstChild) li.appendChild(bp.firstChild)

      ul.appendChild(li)
    }

    // Replace group with <ul>
    group[0].parentNode?.insertBefore(ul, group[0])
    for (const bp of group) bp.remove()
  }
}
